import { Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UserHistoryService } from '@innovation-os-ng/core/auth';
import { ItonicsApp, PermissionService, PermissionVerbsV2, TrackingService } from '@innovation-os-ng/core/core';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { CompoundFilter, EntityFilter, Filter, IqlQueryOperator } from '@itonics/filters';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { TrackedEvents } from '../../constants/tracked-events';
import { FeedWidgetService } from '../../contracts/feed-widget.service';

import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skipper, SpaceSelectors } from '@innovation-os-ng/core/state';
import { Ui2ButtonModule } from '@innovation-os-ng/ui/ui2-button';
import { Store } from '@ngrx/store';
import { FeedRedirectionService } from '../../contracts/feed-redirection.service';
import { EntityFragment } from '../../interfaces/entity';
import { SearchField, SearchFieldIdentifiers } from '../../interfaces/feed';

@Component({
  selector: 'signals-refine-feed-cta',
  templateUrl: './refine-feed-cta.component.html',
  styleUrls: ['./refine-feed-cta.component.scss'],
  standalone: true,
  imports: [CommonModule, Ui2ButtonModule]
})
export class RefineFeedCtaComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly createFilterPermission: PermissionVerbsV2 = PermissionVerbsV2.CREATE_SPACE_PRESET;
  public canCreateFilter: boolean;

  @Input()
  private readonly filter: Filter;

  @Input()
  public linkedSearchField: SearchField;

  // Element (when shown in element's detail)
  @Input()
  public element: EntityFragment;

  // If true, only the 'view in insights' interaction is allowed
  // regardless of the user's premissions
  @Input()
  public viewOnly: boolean;

  // Contextual copy to use for the CTA (defaults exist)
  @Input()
  public copyOverrides: { refineFeed?: string; detailInSignal?: string };

  @Output()
  public onLinkedSearchFieldCreated: EventEmitter<SearchField> = new EventEmitter<SearchField>();

  private onDestroy: Subject<void> = new Subject<void>();
  private currentSpaceUri: string;
  constructor(
    private readonly userHistoryService: UserHistoryService,
    private readonly environmentService: EnvironmentService,
    private readonly trackingService: TrackingService,
    private readonly permissionService: PermissionService,
    private readonly feedWidgetService: FeedWidgetService,
    private readonly feedRedirectionService: FeedRedirectionService,
    private readonly store: Store
  ) {}

  async ngOnInit() {
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (currentSpaceUri: string) => {
          this.currentSpaceUri = currentSpaceUri;
          this.permissionService
            .userHasPermission(this.createFilterPermission, currentSpaceUri)
            .pipe(takeUntil(this.onDestroy))
            .subscribe((permission) => {
              this.canCreateFilter = permission && !this.viewOnly;
            });
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  /**
   * Open the current searchField in insights. There are a few ways this can be accomplished:
   *  - If there's already a linked searchField, open it in insights
   *  - If none exists
   *    - If the user has permission to create searchFields, create one and open it in insights
   *    - Otherwise, just open the default 'implicit' filter in insights (just the element)
   */
  public openInSignals() {
    this.trackingService.trackEventById(TrackedEvents.FEED_CTA_REFINE_FEED, {
      dimension4: this.linkedSearchField ? 'existing' : 'new',
      dimension5: this.canCreateFilter ? 'read/write' : 'read'
    });

    if (this.linkedSearchField || !this.canCreateFilter || !this.element) {
      this.directToInsights();
    } else {
      firstValueFrom(
        this.feedWidgetService.createImplicitSearchField(
          this.element,
          this.composeFilterWithCasualFilter()
            ?.getQuery(SearchFieldIdentifiers.SEARCH_FIELD) // We want to attach the searchField query to the new searchField
            ?.toJson()
        )
      ).then((linkedSearchField: SearchField) => {
        // Open insights with the new linked searchField
        this.linkedSearchField = linkedSearchField;
        this.directToInsights();
        // Inform ancestors of the update
        this.onLinkedSearchFieldCreated.emit(linkedSearchField);
      });
    }
  }

  /**
   * Open insights with either the linked searchField (if one exists) OR the default document filter
   */
  private async directToInsights() {
    this.currentSpaceUri = this.currentSpaceUri || (await this.userHistoryService.getLastVisitedSpace());
    console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null');
    let url = `${this.environmentService.getUrl(ItonicsApp.INSIGHTS)}/${this.currentSpaceUri}`;
    if (this.linkedSearchField) {
      url += `?searchfield=${this.linkedSearchField.uri}`;
      this.feedRedirectionService.redirectToInsights({ url, searchField: this.linkedSearchField });
    } else if (this.filter) {
      const filter = this.generateInsightsFilter();
      if (filter) {
        url += `?filter=${filter.toQueryString()}`;
      }
      this.feedRedirectionService.redirectToInsights({ url, filter });
    } else {
      this.feedRedirectionService.redirectToInsights({ url });
    }
  }

  /**
   * Returns the filter needed to show this feed in insights
   *   - If this.filter is already a top-level insights-like filter (id: 'cb'), that is returned
   *   - Otherwise, this calls this.composeFilterWithCasualFilter to build the correct filter
   *
   * @returns A filter with the correct structure to be used in Insights
   */
  private generateInsightsFilter(): CompoundFilter {
    if (this.filter instanceof CompoundFilter && this.filter?.getIdentifier() === SearchFieldIdentifiers.COMBINED) {
      return this.filter;
    } else {
      return this.composeFilterWithCasualFilter();
    }
  }

  // Insights take entity filters in search-field filter so we need to compose our url as that
  private composeFilterWithCasualFilter(): CompoundFilter {
    const combinedFilter: CompoundFilter = new CompoundFilter(IqlQueryOperator.AND).setIdentifier(SearchFieldIdentifiers.COMBINED);
    const searchFieldFilter: CompoundFilter = new CompoundFilter(IqlQueryOperator.AND).setIdentifier(SearchFieldIdentifiers.SEARCH_FIELD);
    const casualSearchFilter: CompoundFilter = new CompoundFilter(IqlQueryOperator.AND).setIdentifier(SearchFieldIdentifiers.CASUAL);
    const elementFilter: CompoundFilter = new CompoundFilter(IqlQueryOperator.OR).setIdentifier(SearchFieldIdentifiers.ELEMENT);
    searchFieldFilter.addFilter(casualSearchFilter, null, true);
    combinedFilter.addFilter(searchFieldFilter, null, true);
    casualSearchFilter.addFilter(elementFilter, null, true);
    // If entity filter simply add that filter to casual filter else loop and find entity filters and push them
    if (this.filter instanceof EntityFilter) {
      elementFilter.addFilter(this.filter, null, true);
    } else if (this.filter instanceof CompoundFilter) {
      this.filter.getQueries().forEach((filter) => {
        if (filter instanceof EntityFilter) {
          elementFilter.addFilter(filter, null, true);
        }
      });
    }
    return elementFilter.getQueries().length ? combinedFilter : null;
  }
}
